import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { of } from 'rxjs';
import { PromptImportExportDialogComponent } from '../../modules/admin/prompts/dialogs/prompt-import-export-dialog/prompt-import-export-dialog.component';
import { LicenseType } from '../../shared/models/public-payment-info';
import { Rights } from '../../shared/models/rights';
import { TenantRouterService } from '../../shared/services/tenant-router.service';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private readonly tenantRouterService = inject(TenantRouterService);
    private readonly dialog = inject(MatDialog);

    constructor() {
        this.basicNavigationItems.forEach((item) => {
            if (item.link) {
                item.link = this.tenantRouterService.buildUrl([item.link]);
            }
            if (item.children) {
                item.children.forEach((child) => {
                    if (child.link) {
                        child.link = this.tenantRouterService.buildUrl(child.link.split('/'));
                    }
                });
            }
        });
    }

    private basicNavigationItems: FuseNavigationItem[] = [
        {
            id: 'prompts',
            title: 'Prompts',
            type: 'basic',
            icon: 'mat_outline:list_alt',
            link: 'prompts',
        },
        {
            id: 'prompt-import-export',
            title: 'Prompt Import/Export',
            type: 'basic',
            icon: 'mat_outline:import_export',
            function: () =>
                this.dialog.open(PromptImportExportDialogComponent, {
                    panelClass: 'dialog-width',
                }),
        },
        {
            id: 'plugins',
            title: 'Plugins',
            type: 'basic',
            icon: 'mat_outline:extension',
            link: 'plugins',
        },
    ];

    private basicNavigation: Navigation = {
        compact: this.basicNavigationItems,
        default: this.basicNavigationItems,
        futuristic: this.basicNavigationItems,
        horizontal: this.basicNavigationItems,
    };

    public getBasicNavigation() {
        return of(this.basicNavigation);
    }

    public getFullNavigation(licenseType: LicenseType, rights: Rights) {
        const hasAnyAdminRight = Object.values(rights).includes(true);

        const adminNavigationItems: FuseNavigationItem[] = [
            {
                id: 'teams',
                title: 'Teams',
                type: 'basic',
                hidden: (item: FuseNavigationItem) => licenseType === LicenseType.FREE,
                icon: 'mat_outline:groups',
                link: 'teams',
            },
            {
                type: 'divider',
                hidden: () => !hasAnyAdminRight,
            },
            {
                id: 'admin',
                title: 'Admin',
                type: 'group',
                hidden: () => !hasAnyAdminRight,
                children: [
                    {
                        id: 'dashboard',
                        title: 'Dashboard',
                        type: 'basic',
                        icon: 'mat_outline:assessment',
                        link: 'admin/dashboard',
                    },
                    {
                        id: 'publish-prompts',
                        title: 'Öffentliche Prompts',
                        type: 'collapsable',
                        hidden: (item: FuseNavigationItem) => !rights.canAccessPublishPrompts || licenseType === LicenseType.FREE,
                        icon: 'mat_outline:public',
                        children: [
                            {
                                id: 'overview',
                                title: 'Übersicht',
                                type: 'basic',
                                icon: 'mat_outline:view_list',
                                link: 'admin/publish-prompts/overview',
                            },
                            {
                                id: 'settings',
                                title: 'Einstellungen',
                                type: 'basic',
                                icon: 'mat_outline:settings',
                                link: 'admin/publish-prompts/settings',
                            },
                        ],
                    },
                    {
                        id: 'prompt-templates',
                        title: 'Promptvorlagen',
                        type: 'basic',
                        hidden: (item: FuseNavigationItem) => !rights.canAccessPromptTemplates,
                        icon: 'mat_outline:library_books',
                        link: 'admin/prompt-templates',
                    },
                    {
                        id: 'user-management',
                        title: 'Nutzer',
                        type: 'basic',
                        hidden: (item: FuseNavigationItem) => !rights.canAccessUserManagement,
                        icon: 'mat_outline:person',
                        link: 'admin/user-management',
                    },
                    {
                        id: 'user-invites',
                        title: 'Einladungen',
                        type: 'basic',
                        hidden: (item: FuseNavigationItem) => !rights.canAccessUserManagement,
                        icon: 'mat_outline:person_add',
                        link: 'admin/user-invites',
                    },
                    {
                        id: 'microsoft',
                        title: 'Microsoft SSO',
                        type: 'basic',
                        hidden: (item: FuseNavigationItem) =>
                            licenseType !== LicenseType.ENTERPRISE || !rights.canAccessIntegrations,
                        icon: 'mat_outline:vpn_key',
                        link: 'admin/integrations/microsoft',
                    },
                    {
                        id: 'scim',
                        title: 'Nutzersynchronisation\u00A0(SCIM)',
                        type: 'basic',
                        hidden: (item: FuseNavigationItem) =>
                            licenseType !== LicenseType.ENTERPRISE || !rights.canAccessIntegrations,
                        icon: 'mat_outline:sync',
                        link: 'admin/integrations/scim',
                    },
                    {
                        id: 'account-management',
                        title: 'Organisationsverwaltung',
                        type: 'basic',
                        hidden: (item: FuseNavigationItem) => !rights.canAccessAccountManagement,
                        icon: 'mat_outline:tune',
                        link: 'admin/account-management',
                    },
                ],
            },
        ];

        adminNavigationItems.forEach((item) => {
            if (item.link) {
                item.link = this.tenantRouterService.buildUrl([item.link]);
            }
            if (item.children) {
                item.children.forEach((child) => {
                    if (child.link) {
                        child.link = this.tenantRouterService.buildUrl(child.link.split('/'));
                    }

                    if (child.children) {
                        child.children.forEach((grandchild) => {
                            if (grandchild.link) {
                                grandchild.link = this.tenantRouterService.buildUrl(grandchild.link.split('/'));
                            }
                        });
                    }
                });
            }
        });

        const fullNavigationItems: FuseNavigationItem[] = [...this.basicNavigationItems, ...adminNavigationItems];

        const fullNavigation: Navigation = {
            compact: fullNavigationItems,
            default: fullNavigationItems,
            futuristic: fullNavigationItems,
            horizontal: fullNavigationItems,
        };

        return of(fullNavigation);
    }
}
